import { type Control, Controller, type FieldErrors, type UseFormRegister } from 'react-hook-form'

import { ButtonGroup } from '@/components/ui/button-group'
import { CardCheckbox } from '@/components/ui/CardCheckbox'
import { FieldError } from '@/components/ui/field-error'
import { Textarea } from '@/components/ui/textarea'
import { ClaimHowManyVehicles, ClaimWhatDamaged, ClaimWhatHappened } from '../../../../models/claim'
import type { Step3FormValues } from './claimStep3Values'

const WHAT_HAPPENED_OPTIONS: { value: ClaimWhatHappened; label: string }[] = [
  { value: ClaimWhatHappened.TRUCK_HIT_VEHICLE, label: 'My truck hit another vehicle' },
  { value: ClaimWhatHappened.VEHICLE_HIT_TRUCK, label: 'Another vehicle hit my truck' },
  { value: ClaimWhatHappened.ROLLOVER, label: 'My truck rolled over' },
  { value: ClaimWhatHappened.HIT_OBJECT, label: 'I hit something (not a vehicle)' },
  { value: ClaimWhatHappened.CARGO_DAMAGED_LOST, label: 'My cargo was damaged or lost' },
  { value: ClaimWhatHappened.INJURY, label: 'Someone was injured' },
  { value: ClaimWhatHappened.THEFT_VANDALISM, label: 'My truck was stolen or vandalized' },
  { value: ClaimWhatHappened.FIRE_EXPLOSION, label: 'Fire or explosion' },
  { value: ClaimWhatHappened.WEATHER, label: 'Weather damage' },
  { value: ClaimWhatHappened.OTHER, label: 'Something else' },
]

const HOW_MANY_OPTIONS: { value: string; label: string }[] = [
  { value: ClaimHowManyVehicles.TWO_OR_MORE, label: '2 or more vehicles' },
  { value: ClaimHowManyVehicles.MY_VEHICLE_ONLY, label: 'My vehicle only' },
  { value: ClaimHowManyVehicles.NO_VEHICLES, label: 'No vehicles' },
]

const WHAT_DAMAGED_OPTIONS: { value: ClaimWhatDamaged; label: string }[] = [
  { value: ClaimWhatDamaged.MY_TRACTOR, label: 'My tractor' },
  { value: ClaimWhatDamaged.MY_TRAILER, label: 'My trailer' },
  { value: ClaimWhatDamaged.OTHER_VEHICLE, label: 'The other vehicle' },
  { value: ClaimWhatDamaged.PROPERTY, label: 'Property (fence, building, etc.)' },
  { value: ClaimWhatDamaged.MY_CARGO, label: 'My cargo' },
  { value: ClaimWhatDamaged.PERSONAL_VEHICLE, label: 'Personal vehicle' },
]

function SelectAllLabel({ children }: { children: string }) {
  return (
    <label className="text-sm font-medium text-neutral-700">
      {children} <span className="font-normal text-neutral-400">Select all that apply</span>
    </label>
  )
}

interface ClaimWhatHappenedSectionProps {
  control: Control<Step3FormValues>
  register: UseFormRegister<Step3FormValues>
  errors: FieldErrors<Step3FormValues>
}

export function ClaimWhatHappenedSection({ control, register, errors }: ClaimWhatHappenedSectionProps) {
  return (
    <>
      <Controller
        name="whatHappened"
        control={control}
        rules={{ validate: (v) => v.length > 0 || 'Select at least one.' }}
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-2">
            <SelectAllLabel>What best describes what happened? *</SelectAllLabel>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {WHAT_HAPPENED_OPTIONS.map((o) => (
                <CardCheckbox
                  key={o.value}
                  isChecked={field.value.includes(o.value)}
                  onChange={(checked) =>
                    field.onChange(
                      checked ? [...field.value, o.value] : field.value.filter((x) => x !== o.value)
                    )
                  }
                >
                  {o.label}
                </CardCheckbox>
              ))}
            </div>
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />

      <Controller
        name="howManyVehicles"
        control={control}
        rules={{ validate: (v) => !!v || 'Please choose.' }}
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-neutral-700">How many vehicles were involved? *</label>
            <ButtonGroup options={HOW_MANY_OPTIONS} value={field.value} onChange={field.onChange} />
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />

      <Controller
        name="whatDamaged"
        control={control}
        rules={{ validate: (v) => v.length > 0 || 'Select at least one.' }}
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-2">
            <SelectAllLabel>What was damaged? *</SelectAllLabel>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {WHAT_DAMAGED_OPTIONS.map((o) => (
                <CardCheckbox
                  key={o.value}
                  isChecked={field.value.includes(o.value)}
                  onChange={(checked) =>
                    field.onChange(
                      checked ? [...field.value, o.value] : field.value.filter((x) => x !== o.value)
                    )
                  }
                >
                  {o.label}
                </CardCheckbox>
              ))}
            </div>
            <FieldError message={fieldState.error?.message} />
          </div>
        )}
      />

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-neutral-700">Brief description *</label>
        <Textarea
          rows={4}
          placeholder="What happened, in a sentence or two."
          aria-invalid={errors.briefDescription ? true : undefined}
          {...register('briefDescription', {
            validate: (v) => v.trim() !== '' || 'A description is required.',
          })}
        />
        <FieldError message={errors.briefDescription?.message} />
      </div>
    </>
  )
}
